/**
 * WebVTT normalization for caption tracks the SPA fetches as text and hands to
 * a `<track>` as a `blob:` URL.
 *
 * The round trip through the API is what makes this necessary: a blob built
 * from bytes the browser would have rejected as a caption file fails silently —
 * no error event, no cues, just a CC button that does nothing. Checking the
 * signature here turns that into a track we can drop deliberately.
 */

/**
 * Normalize caption text, or `null` when it is not WebVTT at all.
 *
 * Strips a UTF-8 BOM and any leading whitespace (both make the file signature
 * unrecognisable to the parser), and folds CRLF / lone CR to LF. The result
 * must then start with the `WEBVTT` signature, optionally followed by a space,
 * tab, or newline — `WEBVTTX` is not a header, and a caption file that starts
 * with anything else (an HTML error page, an XML subtitle format, an R2 access
 * denied body) is `null`.
 */
export function normalizeVtt(text: string): string | null {
  if (typeof text !== 'string') return null;
  const body = text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .replace(/^\s+/, '');
  if (!/^WEBVTT([ \t\n]|$)/.test(body)) return null;
  return body;
}
